import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FileText,
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  Eye,
  Send,
  Printer,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Share2,
} from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useInvoices, useDeleteInvoice } from '@/hooks/queries/useInvoices';
import { InvoiceForm } from '@/components/invoices/InvoiceForm';
import { toast } from '@/components/ui/toast/useToast';
import type { Invoice } from '@lebusiness/types';

export const Route = createFileRoute('/invoices/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: InvoicesPage,
});

function InvoicesPage() {
  const { company } = useAuthStore();
  const { data: invoices = [], isLoading, error } = useInvoices(company?.id || '');
  const deleteInvoice = useDeleteInvoice();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('fr-DZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const searchLower = searchQuery.toLowerCase();
    
    // Search by invoice number
    const matchesNumber = invoice.invoice_number?.toLowerCase().includes(searchLower) ||
                         invoice.invoiceNumber?.toLowerCase().includes(searchLower);
    
    // Search by client name
    const matchesClient = invoice.client?.name.toLowerCase().includes(searchLower);
    
    // Search by amount (convert to string and check if search query is in it)
    const matchesAmount = invoice.total.toString().includes(searchQuery.replace(/[^0-9]/g, ''));
    
    // Search by product name in items
    const matchesProduct = invoice.items?.some((item: any) => 
      item.description?.toLowerCase().includes(searchLower) ||
      item.product?.name?.toLowerCase().includes(searchLower)
    );
    
    const matchesSearch = matchesNumber || matchesClient || matchesAmount || matchesProduct;
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: invoices.reduce((sum, inv) => sum + inv.total, 0),
    paid: invoices
      .filter((inv) => inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.total, 0),
    pending: invoices
      .filter((inv) => inv.status === 'sent' || inv.status === 'partial')
      .reduce((sum, inv) => sum + inv.balance, 0),
    overdue: invoices
      .filter((inv) => inv.status === 'overdue')
      .reduce((sum, inv) => sum + inv.balance, 0),
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: { label: 'مسودة / Brouillon', color: 'bg-gray-100 text-gray-700' },
      sent: { label: 'مرسلة / Envoyée', color: 'bg-blue-100 text-blue-700' },
      paid: { label: 'مدفوعة / Payée', color: 'bg-emerald-100 text-emerald-700' },
      partial: { label: 'جزئي / Partiel', color: 'bg-yellow-100 text-yellow-700' },
      overdue: { label: 'متأخرة / En retard', color: 'bg-red-100 text-red-700' },
      cancelled: { label: 'ملغاة / Annulée', color: 'bg-red-100 text-red-700' },
    };
    return badges[status as keyof typeof badges] || badges.draft;
  };

  const getTypeLabel = (type: string) => {
    const labels = {
      quote: 'عرض سعر / Devis',
      proforma: 'فاتورة أولية / Proforma',
      invoice: 'فاتورة / Facture',
      credit_note: 'إشعار دائن / Avoir',
      delivery_note: 'وصل تسليم / BL',
    };
    return labels[type as keyof typeof labels] || type;
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      try {
        await deleteInvoice.mutateAsync(id);
        toast.success(
          'تم الحذف بنجاح / Supprimée avec succès',
          'تم حذف الفاتورة / La facture a été supprimée'
        );
        setDeleteConfirmId(null);
      } catch (error) {
        console.error('Failed to delete invoice:', error);
        toast.error('خطأ / Erreur', 'فشل حذف الفاتورة / Échec de la suppression');
      }
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  const handleNew = () => {
    setSelectedInvoice(null);
    setFormOpen(true);
  };

  const handleEdit = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedInvoice(null);
  };

  const handlePrint = (invoiceId: string) => {
    window.open(`/invoices/${invoiceId}`, '_blank');
  };

  const handleShare = async (invoice: Invoice) => {
    const url = `${window.location.origin}/invoices/${invoice.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(
        'تم النسخ / Copié',
        `تم نسخ رابط الفاتورة ${invoice.invoiceNumber}`
      );
    } catch (error) {
      toast.info('الرابط / Lien', url);
    }
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              خطأ في تحميل البيانات / Erreur de chargement
            </h3>
            <p className="text-gray-600">
              {error instanceof Error ? error.message : 'Une erreur est survenue'}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              الفواتير / Factures
            </h1>
            <p className="mt-2 text-gray-600">
              إدارة الفواتير والعروض / Gérez vos factures et devis
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              تصدير / Exporter
            </Button>
            <Button
              size="sm"
              onClick={handleNew}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              فاتورة جديدة / Nouvelle
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">المجموع / Total</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(stats.total)}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">مدفوع / Payé</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(stats.paid)}
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">قيد الانتظار / En attente</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {formatCurrency(stats.pending)}
                  </p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">متأخر / En retard</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(stats.overdue)}
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="بحث برقم الفاتورة، العميل، المبلغ أو المنتج... / Rechercher par numéro, client, montant ou produit..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="flex gap-2">
                {['all', 'draft', 'sent', 'paid', 'overdue'].map((status) => (
                  <Button
                    key={status}
                    variant={statusFilter === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter(status)}
                    className={
                      statusFilter === status ? 'bg-emerald-600 hover:bg-emerald-700' : ''
                    }
                  >
                    {status === 'all' ? 'الكل / Tous' : getStatusBadge(status).label.split(' / ')[1]}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle>
              قائمة الفواتير / Liste des factures ({filteredInvoices.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الرقم / Numéro
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        التاريخ / Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        العميل / Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        النوع / Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        المبلغ / Montant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الحالة / Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        إجراءات / Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInvoices.map((invoice) => {
                      const statusBadge = getStatusBadge(invoice.status);
                      return (
                        <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">
                              {invoice.invoiceNumber}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              {formatDate(invoice.date)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {invoice.client?.name || 'N/A'}
                              </p>
                              {invoice.client?.phone && (
                                <p className="text-xs text-gray-500">{invoice.client.phone}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              {getTypeLabel(invoice.type)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {formatCurrency(invoice.total)}
                              </p>
                              {invoice.balance > 0 && (
                                <p className="text-xs text-red-600">
                                  Reste: {formatCurrency(invoice.balance)}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}
                            >
                              {statusBadge.label.split(' / ')[1]}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Link to="/invoices/$invoiceId" params={{ invoiceId: invoice.id }}>
                                <Button variant="ghost" size="sm" title="عرض / Voir">
                                  <Eye className="w-4 h-4 text-gray-600" />
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(invoice)}
                                title="تعديل / Modifier"
                              >
                                <Edit className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePrint(invoice.id)}
                                title="طباعة / Imprimer"
                              >
                                <Printer className="w-4 h-4 text-purple-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleShare(invoice)}
                                title="مشاركة / Partager"
                              >
                                <Share2 className="w-4 h-4 text-emerald-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(invoice.id)}
                                disabled={deleteInvoice.isPending}
                              >
                                {deleteConfirmId === invoice.id ? (
                                  <span className="text-xs text-red-600 font-medium">
                                    تأكيد؟
                                  </span>
                                ) : (
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && filteredInvoices.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">لا توجد فواتير / Aucune facture trouvée</p>
                <p className="text-sm text-gray-500 mt-1">
                  {invoices.length === 0
                    ? 'ابدأ بإنشاء فاتورة جديدة / Commencez par créer une nouvelle facture'
                    : 'جرب تغيير معايير البحث / Essayez de modifier vos critères'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice Form Modal */}
      <InvoiceForm
        open={formOpen}
        onOpenChange={handleFormClose}
        invoice={selectedInvoice}
        onSuccess={() => {
          handleFormClose();
        }}
      />
    </DashboardLayout>
  );
}
